import { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { Tooltip } from '@material-ui/core';
import { MDSChip, MDSTheme } from '@marqvision/mds/core';
import { IcoCheckOutline, IcoCopyContent } from '@marqvision/mds/assets';

const useStyles = makeStyles({
  tooltip: {
    padding: '0',
    margin: '0',
    backgroundColor: 'transparent',
  },
  link: {
    position: 'relative',
    padding: '1px 4px',
    display: 'inline-block',
    verticalAlign: 'bottom',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-all',
    whiteSpace: 'nowrap',
    ...MDSTheme.typography.medium,
    ...MDSTheme.typography.T12,
    color: MDSTheme.palette.blue[700],
    borderRadius: '4px',
    backgroundColor: MDSTheme.palette.blue[50],
    '&.isOpen': {
      color: MDSTheme.palette.blue[800],
      backgroundColor: MDSTheme.palette.blue[200],
    },
  },
  copyButton: {
    boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 1px 2px 0px rgba(0, 0, 0, 0.04)',
  },
});

type LinkProps = {
  url: string;
  sliceLength?: number;
};

const Link = (props: LinkProps) => {
  const { url, sliceLength } = props;
  const classes = useStyles();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleReset = () => {
    setIsCopied(false);
    setIsOpen(true);
  };

  const handleUrlOpen = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    await window.navigator.clipboard.writeText(url);
    setIsCopied(true);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Tooltip
      open={isOpen}
      interactive
      placement="top"
      classes={{ tooltip: classes.tooltip }}
      leaveDelay={isCopied ? 500 : 0}
      title={(
        <MDSChip
          className={classes.copyButton}
          variant="outline"
          color="bluegray"
          size="small"
          icon={isCopied ? <IcoCheckOutline /> : <IcoCopyContent />}
          label={isCopied ? 'Copied' : 'Copy'}
          onClick={handleCopy}
          onMouseOver={handleOpen}
          onMouseLeave={handleClose}
        />
      )}
    >
        <span
          className={clsx(classes.link, { isOpen })}
          onClick={handleUrlOpen}
          style={{ maxWidth: `${sliceLength}ch` }}
          onMouseOver={handleReset}
          onMouseLeave={handleClose}
        >
          {url}
        </span>
    </Tooltip>
  );
};

type AutoLinkProps = {
  text: string;
  sliceLength?: number;
};

export const AutoLink = (props: AutoLinkProps) => {
  const { text, sliceLength } = props;
  const urlRegExp = /((?:https?:\/\/)?(?:(?:[a-z0-9]?(?:[a-z0-9-]{1,61}[a-z0-9])?\.[^.|\s])+[a-z.]*[a-z]+|(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3})(?::\d{1,5})*[a-z0-9.,_/~#&=;%+?\-\\()]*)/gi;

  return (
    text.split(urlRegExp).map((word, index) => {
      const match = word.match(urlRegExp);

      if (match) {
        const url = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;
        return (
          <Link key={index} url={url} sliceLength={sliceLength} />
        );
      }
      return word;
    })
  );
};